import {
	type MouseEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { api, errorText } from "../api/client"
import type {
	ConnectApp,
	ConnectCode,
	ConnectCodeState,
	Connection,
	Device,
	DevicesResponse,
	HealthStatus,
} from "../api/types"
import { useAuth } from "../auth"
import { agentColor } from "../lib/agents"
import { age } from "../lib/time"
import { currentProject, loadMe, loadSnapshot, toast } from "../store/actions"
import { setUi, useStore } from "../store/store"
import { Swatch, useDismiss } from "./ui"

/**
 * Connect panel. Two paths, and nothing is ever inferred from diffs:
 *
 * A. A computer of yours is online (its connector holds a live socket): pick a
 *    repository and apps, POST /devices/:id/connections, and the connector wires
 *    the hooks over that socket. We then watch /devices for that connection's
 *    fresh health report.
 * B. First-time setup: mint a single-use code, open Claude/Codex with the setup
 *    prompt, and follow GET /connect-codes/:id — the only source of truth for
 *    whether the command ran.
 *
 * Every waiting state shows elapsed time and a next action.
 */

const APPS: Record<
	ConnectApp,
	{ label: string; short: string; sub: string; link: "claude" | "codex" }
> = {
	"claude-code": {
		label: "Claude Code",
		short: "Claude",
		sub: "opens Claude, press Send",
		link: "claude",
	},
	codex: {
		label: "Codex",
		short: "Codex",
		sub: "opens Codex, press Send",
		link: "codex",
	},
}
const APP_IDS = Object.keys(APPS) as ConnectApp[]

const POLL_MS = 1000
const CODE_TTL_MS = 10 * 60_000
/** `?fixtures=1&slow=1` shows the Terminal helper after 3 s instead of 25 s. */
const SLOW = import.meta.env.DEV && new URLSearchParams(location.search).has("slow")
const HELP_AFTER_MS = SLOW ? 3000 : 25_000
const HEALTH_SLOW_MS = 20_000
const HEALTH_GIVE_UP_MS = 45_000
const FIND_GIVE_UP_MS = 30_000
const AGENTS_WATCH_MS = 2 * 60_000
const H_GLYPH: Record<HealthStatus, string> = { ok: "●", warn: "▲", error: "✕" }

const toConnectApp = (a: string): ConnectApp | null =>
	a === "claude-code" ? "claude-code" : a.startsWith("codex") ? "codex" : null

/** A new chat in a folder: claude://code/new?folder=… or codex://threads/new?path=… */
export function chatLink(app: ConnectApp, path: string | null): string {
	const p = path ? encodeURIComponent(path) : null
	return app === "claude-code"
		? `claude://code/new${p ? `?folder=${p}` : ""}`
		: `codex://threads/new${p ? `?path=${p}` : ""}`
}

/** Opens a deep link in the desktop app. Fixtures hand out "#", which opens nothing. */
function openLink(link: string | null): boolean {
	if (!link || link === "#") return false
	window.location.href = link
	return true
}

const mmss = (ms: number) => {
	const s = Math.max(0, Math.floor(ms / 1000))
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

/** Server expiry, trusted only if it looks sane against our own clock. */
function deadline(expiresAt: string, receivedAt: number) {
	const ttl = Date.parse(expiresAt) - receivedAt
	return receivedAt + (ttl > 0 && ttl <= 2 * CODE_TTL_MS ? ttl : CODE_TTL_MS)
}

const isOnline = (d: Device, live: string[], liveDevices: string[] = []) =>
	liveDevices.includes(d.id) || d.connections.some(c => live.includes(c.id))

type Repo = NonNullable<NonNullable<Device["inventory"]>["repos"]>[number]

function sortedRepos(d: Device): Repo[] {
	return [...(d.inventory?.repos ?? [])].sort((a, b) =>
		(b.lastActiveAt ?? "").localeCompare(a.lastActiveAt ?? ""),
	)
}

function findConn(data: DevicesResponse | null, id: string | null) {
	if (!data || !id) return null
	for (const device of data.devices) {
		const conn = device.connections.find(c => c.id === id)
		if (conn) return { device, conn }
	}
	return null
}

function useNow(active: boolean) {
	const [now, setNow] = useState(Date.now())
	useEffect(() => {
		if (!active) return
		setNow(Date.now())
		const t = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(t)
	}, [active])
	return now
}

function useInterval(cb: () => void, ms: number | null) {
	const ref = useRef(cb)
	ref.current = cb
	useEffect(() => {
		if (ms == null) return
		const t = setInterval(() => ref.current(), ms)
		return () => clearInterval(t)
	}, [ms])
}

function useFocusOnMount<T extends HTMLElement>() {
	const ref = useRef<T>(null)
	useEffect(() => ref.current?.focus(), [])
	return ref
}

/** Result of POST /devices/:id/connections plus what we need to follow it. */
interface Attach {
	deviceId: string
	deviceName: string
	connectionId: string
	delivered: boolean
	repo: { name: string; path: string }
	apps: ConnectApp[]
	/** health.checkedAt before the click: a different value means a fresh report. */
	baseline: string | null
	at: number
}

type Flow =
	| { k: "idle" }
	| {
			k: "setup"
			app: ConnectApp
			code: ConnectCode
			receivedAt: number
			opened: boolean
	  }
	| { k: "attached"; a: Attach }

type Announce = (text: string) => void

export function ConnectPanel() {
	const close = useCallback(() => setUi({ connect: false }), [])
	useDismiss(close)
	const project = useStore(s => currentProject(s))
	const firstRun = useStore(s => !s.loading && Object.keys(s.agents).length === 0)
	const auth = useAuth()
	const latest = auth.config?.connectorVersion ?? null
	const [data, setData] = useState<DevicesResponse | null>(null)
	const [err, setErr] = useState<string | null>(null)
	const [flow, setFlow] = useState<Flow>({ k: "idle" })
	const [forceSetup, setForceSetup] = useState(false)
	const [fallback, setFallback] = useState<{ note: string; path: string | null } | null>(
		null,
	)
	const [busy, setBusy] = useState<ConnectApp | null>(null)
	const [deviceId, setDeviceId] = useState<string | null>(null)
	const [live, setLive] = useState("")

	const load = useCallback(async () => {
		try {
			setData(await api.devices())
			setErr(null)
		} catch (e) {
			setErr(errorText(e))
		}
	}, [])

	useEffect(() => {
		void load()
	}, [load])

	const online = useMemo(() => {
		if (!data) return []
		const here = (d: Device) =>
			Number(d.connections.some(c => c.project_id === project?.id))
		return data.devices
			.filter(d => isOnline(d, data.live, data.liveDevices))
			.sort(
				(a, b) =>
					here(b) - here(a) || (b.last_seen_at ?? "").localeCompare(a.last_seen_at ?? ""),
			)
	}, [data, project?.id])
	const device = online.find(d => d.id === deviceId) ?? online[0] ?? null
	const modeA = !!device && !forceSetup && !fallback

	const focusPrimary = () =>
		requestAnimationFrame(() =>
			document.querySelector<HTMLElement>(".slide [data-primary]")?.focus(),
		)

	// Focus the primary control once the panel knows which path to show.
	const focusedOnce = useRef(false)
	useEffect(() => {
		if (!data || focusedOnce.current) return
		focusedOnce.current = true
		focusPrimary()
	}, [data])

	const toIdle = () => {
		setFlow({ k: "idle" })
		void load()
		focusPrimary()
	}

	const start = async (app: ConnectApp, open: boolean, path?: string | null) => {
		if (!project) return
		setBusy(app)
		setLive(open ? `Opening ${APPS[app].short}…` : "Getting a setup command…")
		try {
			const code = await api.connectCode(project.id, { app, ...(path ? { path } : {}) })
			if (open) openLink(code.links[APPS[app].link])
			setFlow({ k: "setup", app, code, receivedAt: Date.now(), opened: open })
			setLive(
				open
					? `${APPS[app].short} opened. Press Send there.`
					: "Paste the setup command in Terminal.",
			)
		} catch (e) {
			toast(`Could not start setup: ${errorText(e)}`)
			setLive("Could not start setup")
		} finally {
			setBusy(null)
		}
	}

	const onPicked = (a: Attach) => {
		if (!a.delivered) {
			setFallback({
				note: `${a.deviceName} didn’t answer — it looks offline. Connect from a new chat instead:`,
				path: a.repo.path,
			})
			setLive(`${a.deviceName} is offline. Connect from a new chat instead.`)
			void load()
			focusPrimary()
			return
		}
		setFlow({ k: "attached", a })
		setLive(`Connected ${a.repo.name} on ${a.deviceName}. Installing hooks.`)
	}

	return (
		<>
			<div className="scrim clear" onMouseDown={close} />
			<aside className="slide" role="dialog" aria-label="Connect agents">
				<div className="slide-h">
					Connect agents{project && <span className="dim"> to {project.name}</span>}
					<button className="icon-btn" aria-label="Close (Esc)" onClick={close}>
						×
					</button>
				</div>
				<div className="sr" role="status" aria-live="polite">
					{live}
				</div>
				<div className="slide-body">
					{err && (
						<div className="row form-error" role="alert">
							<span className="grow">Could not load your computers: {err}</span>
							<button className="btn btn-xs" onClick={() => void load()}>
								Retry
							</button>
						</div>
					)}
					{!project ? (
						<div className="dim">Open a project first.</div>
					) : flow.k === "setup" ? (
						<Setup
							key={flow.code.id}
							app={flow.app}
							code={flow.code}
							receivedAt={flow.receivedAt}
							opened={flow.opened}
							data={data}
							reload={load}
							announce={setLive}
							onCancel={() => {
								setLive("Setup cancelled")
								toIdle()
							}}
							onRestart={app => void start(app, flow.opened)}
							onClose={close}
							onAnother={() => {
								setForceSetup(false)
								setFallback(null)
								toIdle()
							}}
						/>
					) : flow.k === "attached" ? (
						<section className="blk" aria-labelledby="cx-att-h">
							<Attached
								a={flow.a}
								data={data}
								reload={load}
								announce={setLive}
								onDone={close}
								onAnother={toIdle}
							/>
						</section>
					) : !data && !err ? (
						<div className="loading-bar" aria-label="Loading your computers" />
					) : modeA ? (
						<OnDevice
							devices={online}
							device={device}
							onDevice={setDeviceId}
							reload={load}
							onResult={onPicked}
							onSetup={() => {
								setForceSetup(true)
								focusPrimary()
							}}
						/>
					) : (
						<>
							{fallback && (
								<div className="cx-fallback" role="status">
									{fallback.note}
								</div>
							)}
							<Hero
								busy={busy}
								big={firstRun || !!fallback}
								path={fallback?.path ?? null}
								onClearPath={() => setFallback(f => (f ? { ...f, path: null } : f))}
								onPick={app => void start(app, true, fallback?.path)}
							/>
							<OtherWays
								busy={busy}
								onTerminal={app => void start(app, false, fallback?.path)}
							/>
							{device && (
								<button
									className="cx-link cx-alt"
									onClick={() => {
										setForceSetup(false)
										setFallback(null)
										focusPrimary()
									}}
								>
									← Back to {device.name}
								</button>
							)}
						</>
					)}
					{flow.k === "idle" && !!data?.devices.length && (
						<Devices data={data} latest={latest} reload={load} />
					)}
					{flow.k === "idle" && modeA && (
						<button
							className="cx-link cx-alt"
							onClick={() => {
								setForceSetup(true)
								focusPrimary()
							}}
						>
							Set up another computer
						</button>
					)}
				</div>
			</aside>
		</>
	)
}

// --- glyphs & small bits ----------------------------------------------------------------

export function Glyph({ app }: { app: ConnectApp }) {
	return app === "claude-code" ? (
		<svg className="cx-glyph claude" viewBox="0 0 20 20" aria-hidden>
			<path
				d="M10 2v16M2 10h16M4.3 4.3l11.4 11.4M15.7 4.3 4.3 15.7"
				stroke="currentColor"
				strokeWidth="2.2"
				strokeLinecap="round"
			/>
		</svg>
	) : (
		<svg className="cx-glyph codex" viewBox="0 0 20 20" aria-hidden>
			<rect
				x="2"
				y="3"
				width="16"
				height="14"
				rx="3.5"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.6"
			/>
			<path
				d="m6 8 2.5 2L6 12M10.5 12.5H14"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

function Copy(props: {
	text: string
	label: string
	className?: string
	autoFocus?: boolean
}) {
	const [done, setDone] = useState(false)
	const ref = useRef<HTMLButtonElement>(null)
	useEffect(() => {
		if (props.autoFocus) ref.current?.focus()
	}, [props.autoFocus])
	return (
		<button
			ref={ref}
			className={props.className ?? "btn btn-xs"}
			onClick={() => {
				void navigator.clipboard
					.writeText(props.text)
					.then(() => {
						setDone(true)
						setTimeout(() => setDone(false), 1500)
					})
					.catch(() => toast("Could not copy — select the text and copy it"))
			}}
		>
			<span aria-live="polite">{done ? "Copied ✓" : props.label}</span>
		</button>
	)
}

/** Deep links never open anything in fixtures ("#"). */
const guardHash = (e: MouseEvent<HTMLAnchorElement>) => {
	if (e.currentTarget.getAttribute("href") === "#") e.preventDefault()
}

function FollowUps({ apps, path }: { apps: ConnectApp[]; path: string | null }) {
	return (
		<div className="blk-row cx-col cx-follow">
			<div className="cx-follow-btns">
				{apps.map(app => (
					<a key={app} className="btn cx-open" href={chatLink(app, path)}>
						<Glyph app={app} />
						{path
							? `Open a new ${APPS[app].label} chat here`
							: `Open a new ${APPS[app].label} chat`}
					</a>
				))}
			</div>
			<span className="dim cx-small">New chats join as their own agents.</span>
		</div>
	)
}

function AgentChips({ agents }: { agents: ConnectCodeState["agents"] }) {
	const all = useStore(s => s.agents)
	return (
		<>
			{agents.map(a => {
				const v = all[a.id]
				return (
					<span key={a.id} className="chip cx-agent">
						{v && <Swatch agent={v} />}
						<span style={v ? { color: agentColor(v.color) } : undefined}>
							@{a.handle}
						</span>
					</span>
				)
			})}
		</>
	)
}

// --- A: a computer that is already connected ---------------------------------------------

function OnDevice(props: {
	devices: Device[]
	device: Device
	onDevice: (id: string) => void
	reload: () => Promise<void>
	onResult: (a: Attach) => void
	onSetup: () => void
}) {
	const { device } = props
	const [looking, setLooking] = useState(false)
	const repos = sortedRepos(device)
	return (
		<section className="blk cx-on" aria-labelledby="cx-on-h">
			<div className="blk-h">
				<span className="sockdot sock-live" role="img" aria-label="online" />
				<h3 id="cx-on-h" className="cx-h">
					On{" "}
					{props.devices.length > 1 ? (
						<select
							className="select cx-dev-select"
							aria-label="Computer"
							value={device.id}
							onChange={e => props.onDevice(e.target.value)}
						>
							{props.devices.map(d => (
								<option key={d.id} value={d.id}>
									{d.name}
								</option>
							))}
						</select>
					) : (
						<b>{device.name}</b>
					)}
				</h3>
				<span className="grow" />
				<span className="dim cx-small">online</span>
			</div>
			{repos.length ? (
				<RepoPicker
					key={device.id}
					device={device}
					label="Connect"
					onResult={props.onResult}
				/>
			) : (
				<div className="blk-row cx-col">
					<span className="dim">
						No recent repositories on {device.name} yet. Open Claude Code or Codex in a
						repository once, then look again.
					</span>
					<div className="row">
						<button
							className="btn btn-xs"
							data-primary
							disabled={looking}
							onClick={() => {
								setLooking(true)
								void props.reload().finally(() => setLooking(false))
							}}
						>
							{looking ? "Looking…" : "Look again"}
						</button>
						<button className="btn btn-xs btn-ghost" onClick={props.onSetup}>
							Connect from a new chat instead
						</button>
					</div>
				</div>
			)}
		</section>
	)
}

function defaultApps(repo: Repo | undefined, device: Device): ConnectApp[] {
	const fromRepo = [...new Set((repo?.apps ?? []).map(toConnectApp).filter(Boolean))]
	if (fromRepo.length) return APP_IDS.filter(a => fromRepo.includes(a))
	const found = (device.inventory?.apps ?? [])
		.filter(a => a.found)
		.map(a => toConnectApp(a.id))
	const fromDevice = APP_IDS.filter(a => found.includes(a))
	return fromDevice.length ? fromDevice : ["claude-code"]
}

function RepoPicker(props: {
	device: Device
	label: string
	/** Fixed apps hide the chips (first-time setup connects the app it set up). */
	fixedApps?: ConnectApp[]
	onResult: (a: Attach) => void
}) {
	const { device } = props
	const project = useStore(s => currentProject(s))
	const repos = useMemo(() => sortedRepos(device), [device])
	const existing = device.connections.find(c => c.project_id === project?.id) ?? null
	const isConnected = (r: Repo) =>
		!!existing &&
		(existing.paths.includes(r.path) || (!!r.repoKey && existing.repo_key === r.repoKey))
	const [path, setPath] = useState<string | null>(null)
	const role = useStore(s => s.role)
	// The project's own repository: the checkout of it on this computer is the default.
	const projectRepo = project?.githubRepos[0] ?? null
	const githubOf = (r: Repo) =>
		r.repoKey?.match(/^github\.com\/([\w.-]+\/[\w.-]+)$/i)?.[1]?.toLowerCase() ?? null
	const isProjectRepo = (r: Repo) => !!projectRepo && githubOf(r) === projectRepo
	const repo =
		repos.find(r => r.path === path) ??
		(existing?.repo_key ? repos.find(r => r.repoKey === existing.repo_key) : undefined) ??
		repos.find(r => existing?.paths.includes(r.path)) ??
		repos.find(isProjectRepo) ??
		repos[0]
	// A project with no repository yet takes the one its first chats work in.
	const adopt = repo && !projectRepo && role === "admin" ? githubOf(repo) : null
	const [adoptOn, setAdoptOn] = useState(true)
	const [apps, setApps] = useState<ConnectApp[] | null>(null)
	const chosen = props.fixedApps ?? apps ?? defaultApps(repo, device)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	if (!repo || !project) return null

	const go = async () => {
		setBusy(true)
		setError(null)
		try {
			const r = await api.connect(device.id, {
				projectId: project.id,
				path: repo.path,
				...(repo.repoKey ? { repoKey: repo.repoKey } : {}),
				apps: chosen,
				autoShare: true,
			})
			if (adopt && adoptOn) {
				await api
					.patchProject(project.id, { githubRepos: [adopt] })
					.then(() =>
						api.githubSetup(project.id, adopt, device.id).catch(() => undefined),
					)
					.then(() => loadMe())
					.catch(e => toast(errorText(e)))
			}
			setBusy(false)
			props.onResult({
				deviceId: device.id,
				deviceName: device.name,
				connectionId: r.connectionId,
				delivered: r.delivered,
				repo: { name: repo.name, path: repo.path },
				apps: chosen,
				baseline: existing?.health?.checkedAt ?? null,
				at: Date.now(),
			})
		} catch (e) {
			setBusy(false)
			setError(errorText(e))
		}
	}

	const now = Date.now()
	return (
		<>
			<div className="blk-row cx-col">
				<label className="lbl" htmlFor={`cx-repo-${device.id}`}>
					Repository
				</label>
				<select
					id={`cx-repo-${device.id}`}
					className="select cx-select"
					data-primary
					value={repo.path}
					onChange={e => {
						setPath(e.target.value)
						setApps(null)
					}}
				>
					{repos.map(r => (
						<option key={r.path} value={r.path}>
							{r.name}
							{r.lastActiveAt ? ` · ${age(r.lastActiveAt, now)} ago` : ""}
							{isProjectRepo(r) ? " · this project's repo" : ""}
							{isConnected(r) ? " · connected" : ""}
						</option>
					))}
				</select>
				<span className="mono dim ell cx-small" title={repo.path}>
					{repo.path}
				</span>
				{projectRepo && !repos.some(isProjectRepo) && (
					<span className="warn cx-small">
						This project's repo is {projectRepo}, and no folder here that Claude or Codex
						has worked in is a copy of it. Open a chat in your copy first, or pick another
						folder.
					</span>
				)}
				{adopt && (
					<label className="cx-small cx-adopt">
						<input
							type="checkbox"
							checked={adoptOn}
							onChange={e => setAdoptOn(e.target.checked)}
						/>
						<span>
							Make <b className="mono">{adopt}</b> this project's GitHub repo (merges,
							checks and reviews show up in the rooms)
						</span>
					</label>
				)}
			</div>
			{!props.fixedApps && (
				<div className="blk-row">
					<span className="lbl" id={`cx-apps-${device.id}`}>
						Apps
					</span>
					<div className="cx-chips" role="group" aria-labelledby={`cx-apps-${device.id}`}>
						{APP_IDS.map(a => {
							const on = chosen.includes(a)
							return (
								<button
									key={a}
									className="cx-chip"
									aria-pressed={on}
									onClick={() =>
										setApps(
											on
												? chosen.filter(x => x !== a)
												: APP_IDS.filter(x => x === a || chosen.includes(x)),
										)
									}
								>
									<Glyph app={a} />
									{APPS[a].label}
								</button>
							)
						})}
					</div>
				</div>
			)}
			<div className="blk-row">
				<button
					className="btn btn-primary cx-go"
					disabled={busy || !chosen.length}
					aria-busy={busy}
					onClick={() => void go()}
				>
					{busy && <span className="spin" aria-hidden />}
					{busy ? "Connecting…" : props.label}
				</button>
				<span className="dim cx-small ell">
					{!chosen.length
						? "Pick at least one app"
						: isConnected(repo)
							? "Already connected — this re-checks the hooks"
							: `Installs ${chosen.map(a => APPS[a].label).join(" + ")} hooks in ${repo.name}`}
				</span>
			</div>
			{error && (
				<div className="blk-row form-error" role="alert">
					Could not connect: {error}
				</div>
			)}
		</>
	)
}

/** After a delivered connect: watch for the connector's fresh health report, then offer chats. */
function Attached(props: {
	a: Attach
	data: DevicesResponse | null
	reload: () => Promise<void>
	announce: Announce
	onDone: () => void
	onAnother: () => void
}) {
	const { a } = props
	const heading = useFocusOnMount<HTMLHeadingElement>()
	const conn = findConn(props.data, a.connectionId)?.conn ?? null
	const health = conn?.health ?? null
	const fresh =
		a.delivered &&
		!!health &&
		(health.checkedAt ?? null) !== a.baseline &&
		a.apps.every(x => health.apps?.[x])
	const [skipped, setSkipped] = useState(false)
	const [repairing, setRepairing] = useState(false)
	const now = useNow(a.delivered && !fresh)
	const elapsed = now - a.at
	const gaveUp = a.delivered && !fresh && elapsed >= HEALTH_GIVE_UP_MS
	useInterval(
		() => void props.reload(),
		a.delivered && !fresh && !gaveUp ? POLL_MS : null,
	)

	const announce = useRef(props.announce)
	announce.current = props.announce
	useEffect(() => {
		if (fresh) {
			const bad = a.apps.filter(x => health?.apps?.[x]?.status !== "ok")
			announce.current(
				bad.length
					? `Connected with warnings for ${bad.map(x => APPS[x].label).join(", ")}`
					: `Hooks ready on ${a.deviceName}`,
			)
		} else if (gaveUp) announce.current(`No hook report from ${a.deviceName} yet`)
	}, [fresh, gaveUp])

	const repair = async () => {
		setRepairing(true)
		try {
			const r = await api.repair(a.connectionId)
			toast(
				r.sent ? "Repair sent" : `${a.deviceName} is offline`,
				r.sent ? "info" : "error",
			)
		} catch (e) {
			toast(errorText(e))
		} finally {
			setRepairing(false)
		}
	}
	const repairBtn = (
		<button className="btn btn-xs" disabled={repairing} onClick={() => void repair()}>
			{repairing ? "Sending…" : "Repair"}
		</button>
	)

	return (
		<>
			<div className="blk-h">
				<span className="cx-ok" aria-hidden>
					✓
				</span>
				<h3 id="cx-att-h" className="cx-h" tabIndex={-1} ref={heading}>
					Connected {a.repo.name}
					<span className="dim"> on {a.deviceName}</span>
				</h3>
			</div>
			<div className="blk-row cx-col">
				{!a.delivered ? (
					<span className="health h-warn">
						▲{" "}
						<span className="dim2">
							Saved. {a.deviceName} applies it when its connector is back online.
						</span>
					</span>
				) : fresh ? (
					a.apps.map(x => {
						const h = health!.apps![x]!
						return (
							<div key={x} className={"health cx-hl h-" + h.status}>
								<span aria-hidden>{H_GLYPH[h.status]}</span>
								<b className="dim2">{APPS[x].label}</b>
								<span className="dim2 cx-detail">{h.detail}</span>
								<span className="sr">{h.status}</span>
							</div>
						)
					})
				) : gaveUp ? (
					<div className="row cx-wrap">
						<span className="health h-warn">▲</span>
						<span className="dim2 grow">
							No hook report from {a.deviceName} after {mmss(elapsed)}. It may still be
							installing.
						</span>
						{repairBtn}
					</div>
				) : (
					<>
						<div className="row">
							<span className="spin" aria-hidden />
							<span className="dim2">Installing hooks on {a.deviceName}…</span>
							<span className="grow" />
							<span
								className="mono dim"
								aria-label={`${Math.floor(elapsed / 1000)} seconds`}
							>
								{mmss(elapsed)}
							</span>
						</div>
						<div className="row cx-wrap">
							{elapsed >= HEALTH_SLOW_MS && (
								<>
									<span className="dim">Taking longer than usual.</span>
									{repairBtn}
								</>
							)}
							{!skipped && (
								<button className="cx-link" onClick={() => setSkipped(true)}>
									Don’t wait — show chat links
								</button>
							)}
						</div>
					</>
				)}
			</div>
			{(fresh || skipped || gaveUp || !a.delivered) && (
				<FollowUps apps={a.apps} path={a.repo.path} />
			)}
			<div className="blk-row">
				<button className="btn btn-xs btn-primary" onClick={props.onDone}>
					Done
				</button>
				<button className="btn btn-xs btn-ghost" onClick={props.onAnother}>
					Connect another repository
				</button>
			</div>
		</>
	)
}

// --- B: first-time setup ------------------------------------------------------------------

function Hero(props: {
	busy: ConnectApp | null
	big: boolean
	path: string | null
	onPick: (app: ConnectApp) => void
	onClearPath: () => void
}) {
	return (
		<div className={"cx-hero" + (props.big ? " big" : "")}>
			<div className="cx-btns" role="group" aria-label="Connect an agent app">
				{APP_IDS.map((app, i) => (
					<button
						key={app}
						className="cx-btn"
						data-primary={i === 0 ? true : undefined}
						disabled={!!props.busy}
						aria-busy={props.busy === app}
						onClick={() => props.onPick(app)}
					>
						{props.busy === app ? (
							<span className="spin" aria-hidden />
						) : (
							<Glyph app={app} />
						)}
						<span className="cx-btn-t">
							<b>Connect {APPS[app].label}</b>
							<span>
								{props.busy === app ? `opening ${APPS[app].short}…` : APPS[app].sub}
							</span>
						</span>
					</button>
				))}
			</div>
			<div className="row dim cx-note">
				{props.path ? (
					<>
						<span>Opens in</span>
						<span className="mono dim2 ell" title={props.path}>
							{props.path}
						</span>
						<button
							className="btn btn-xs btn-ghost"
							aria-label="Do not open a specific folder"
							onClick={props.onClearPath}
						>
							×
						</button>
					</>
				) : (
					"Sets up this computer from a new chat. Each chat joins as its own agent."
				)}
			</div>
		</div>
	)
}

function OtherWays(props: {
	busy: ConnectApp | null
	onTerminal: (app: ConnectApp) => void
}) {
	return (
		<details className="cx-other">
			<summary>Other ways</summary>
			<div className="blk">
				<div className="blk-row">
					<span className="dim grow">Run the setup in Terminal for</span>
					{APP_IDS.map(a => (
						<button
							key={a}
							className="btn btn-xs"
							disabled={!!props.busy}
							onClick={() => props.onTerminal(a)}
						>
							<Glyph app={a} /> {APPS[a].label}
						</button>
					))}
				</div>
				<div className="blk-row">
					<a className="btn btn-xs" href="/download/walkie-connector.pkg">
						Download installer (.pkg, macOS)
					</a>
					<span className="dim ell">for Claude Desktop chat</span>
				</div>
			</div>
		</details>
	)
}

type StepState = "done" | "active" | "todo" | "failed"

function Step(props: {
	state: StepState
	n: number
	title: ReactNode
	aside?: ReactNode
	children?: ReactNode
}) {
	const { state } = props
	return (
		<li
			className={"cx-step " + state}
			aria-current={state === "active" ? "step" : undefined}
		>
			<span className="cx-step-i" aria-hidden>
				{state === "done" ? (
					"✓"
				) : state === "active" ? (
					<span className="spin" />
				) : state === "failed" ? (
					"✕"
				) : (
					props.n
				)}
			</span>
			<div className="cx-step-b">
				<div className="row">
					<b className="cx-step-t">{props.title}</b>
					<span className="sr">
						{state === "done"
							? " — done"
							: state === "active"
								? " — in progress"
								: state === "failed"
									? " — failed"
									: " — not yet"}
					</span>
					<span className="grow" />
					{props.aside}
				</div>
				{props.children}
			</div>
		</li>
	)
}

function Setup(props: {
	app: ConnectApp
	code: ConnectCode
	receivedAt: number
	opened: boolean
	data: DevicesResponse | null
	reload: () => Promise<void>
	announce: Announce
	onCancel: () => void
	onRestart: (app: ConnectApp) => void
	onClose: () => void
	onAnother: () => void
}) {
	const { app, code, opened } = props
	const meta = APPS[app]
	const heading = useFocusOnMount<HTMLHeadingElement>()
	const [st, setSt] = useState<ConnectCodeState | null>(null)
	const [pollErr, setPollErr] = useState<string | null>(null)
	const [helpOpen, setHelpOpen] = useState<"auto" | "user" | null>(opened ? null : "user")
	const [connectedAt, setConnectedAt] = useState<number | null>(null)
	const [lookFrom, setLookFrom] = useState<number | null>(null)
	const [attach, setAttach] = useState<Attach | null>(null)
	const until = deadline(st?.expiresAt ?? code.expiresAt, props.receivedAt)
	const now = useNow(true)
	const status =
		st?.status === "waiting" || !st
			? now >= until + 5000
				? "expired"
				: "waiting"
			: st.status
	const elapsed = now - props.receivedAt
	const link = code.links[meta.link]

	const announce = useRef(props.announce)
	announce.current = props.announce

	const poll = async () => {
		try {
			setSt(await api.connectCodeStatus(code.id))
			setPollErr(null)
		} catch (e) {
			setPollErr(errorText(e))
		}
	}
	useEffect(() => {
		void poll()
	}, [])
	const watchAgents =
		status === "connected" && connectedAt != null && now - connectedAt < AGENTS_WATCH_MS
	useInterval(
		() => void poll(),
		status === "waiting" ? POLL_MS : watchAgents ? 3000 : null,
	)

	useEffect(() => {
		if (status === "connected") {
			const at = Date.now()
			setConnectedAt(at)
			setLookFrom(at)
			announce.current(
				`Connected ${st?.deviceName ?? "this computer"} to ${st?.projectName ?? "the project"}`,
			)
			void loadSnapshot()
			void props.reload()
		} else if (status === "expired") announce.current("This setup link expired")
	}, [status])

	const agentKey = st?.agents.map(a => a.id).join(",") ?? ""
	useEffect(() => {
		if (agentKey) void loadSnapshot()
	}, [agentKey])

	const helpDue = status === "waiting" && elapsed >= HELP_AFTER_MS
	useEffect(() => {
		if (helpDue && opened) {
			setHelpOpen(h => h ?? "auto")
			announce.current(
				`${meta.short} didn’t run it? Paste the command in Terminal instead.`,
			)
		}
	}, [helpDue])

	// After connecting: find the device (and its repositories when the chat had no folder).
	const found =
		status === "connected" ? findConn(props.data, st?.connectionId ?? null) : null
	const repos = found ? sortedRepos(found.device) : []
	const hasPaths = !!found?.conn.paths.length
	const sinceLook = lookFrom ? now - lookFrom : 0
	const needLook =
		status === "connected" && !attach && (!found || (!hasPaths && !repos.length))
	const looking = needLook && sinceLook < FIND_GIVE_UP_MS
	useInterval(() => void props.reload(), looking ? POLL_MS : null)

	const deviceName = st?.deviceName ?? found?.device.name ?? "this computer"
	const connected = status === "connected"
	const expired = status === "expired"

	return (
		<>
			<section className="blk cx-setup" aria-labelledby="cx-setup-h">
				<div className="blk-h">
					{expired ? (
						<span className="cx-x" aria-hidden>
							✕
						</span>
					) : (
						<Glyph app={app} />
					)}
					<h3 id="cx-setup-h" className="cx-h" tabIndex={-1} ref={heading}>
						{expired
							? "This setup link expired"
							: connected
								? `Connected to ${st?.projectName ?? "the project"}`
								: `Connecting ${meta.label}`}
					</h3>
					<span className="grow" />
					{status === "waiting" && (
						<span
							className="mono dim cx-small"
							title="The setup link works once, for 10 minutes"
						>
							link expires in {mmss(until - now)}
						</span>
					)}
				</div>
				{expired ? (
					<div className="blk-row cx-col">
						<span className="dim2">
							Setup links work once, for 10 minutes. Nothing was changed on your computer.
						</span>
						<div className="row">
							<button className="btn btn-primary" onClick={() => props.onRestart(app)}>
								Start again
							</button>
							<button className="btn btn-ghost" onClick={props.onCancel}>
								Cancel
							</button>
						</div>
					</div>
				) : (
					<ol className="cx-steps">
						<Step
							state="done"
							n={1}
							title={
								opened
									? `${meta.short} opened — press Send there`
									: "Paste the command in Terminal"
							}
						>
							{opened && status === "waiting" && link && (
								<a className="cx-link" href={link} onClick={guardHash}>
									Didn’t open? Open again
								</a>
							)}
						</Step>
						<Step
							state={connected ? "done" : "active"}
							n={2}
							title="Setup command runs"
							aside={
								!connected && (
									<span
										className="mono dim"
										aria-label={`waiting ${Math.floor(elapsed / 1000)} seconds`}
									>
										{mmss(elapsed)}
									</span>
								)
							}
						>
							{connected ? (
								<span className="dim">
									Ran on <b className="dim2">{deviceName}</b>
								</span>
							) : (
								<>
									<span className="dim">
										{opened
											? `Waiting for ${meta.short} to run it. Check the chat for a permission prompt.`
											: "Waiting for the command to finish…"}
									</span>
									{pollErr && (
										<span className="form-error">
											Can’t reach Walkie ({pollErr}) — retrying.
										</span>
									)}
									{helpOpen ? (
										<div
											className="cx-help"
											role="group"
											aria-label="Run the setup in Terminal"
										>
											<div className="dim2">
												{opened
													? `${meta.short} didn’t run it? Paste this in Terminal instead:`
													: "Paste this in Terminal:"}
											</div>
											<pre className="cmd cx-prompt">{code.command}</pre>
											<div className="row cx-wrap">
												<Copy
													text={code.command}
													label="Copy command"
													className="btn btn-primary cx-copy"
													autoFocus={helpOpen === "user"}
												/>
												{opened && (
													<Copy
														text={code.prompt}
														label="or copy the prompt"
														className="btn btn-ghost btn-xs"
													/>
												)}
											</div>
										</div>
									) : (
										<button className="cx-link" onClick={() => setHelpOpen("user")}>
											Use Terminal instead
										</button>
									)}
								</>
							)}
						</Step>
						<Step state={connected ? "done" : "todo"} n={3} title="Connected">
							{connected && (
								<div className="row cx-wrap">
									<span className="dim2">{deviceName}</span>
									{st?.agents.length ? (
										<AgentChips agents={st.agents} />
									) : (
										<span className="dim">
											· chats join as agents as soon as they run
										</span>
									)}
								</div>
							)}
						</Step>
					</ol>
				)}
				{!connected && !expired && (
					<div className="blk-row">
						<span className="grow" />
						<button className="btn btn-xs btn-ghost" onClick={props.onCancel}>
							Cancel
						</button>
					</div>
				)}
			</section>

			{connected &&
				(attach ? (
					<section className="blk" aria-labelledby="cx-att-h">
						<Attached
							a={attach}
							data={props.data}
							reload={props.reload}
							announce={props.announce}
							onDone={props.onClose}
							onAnother={props.onAnother}
						/>
					</section>
				) : (
					<section className="blk" aria-label="Next">
						{found && hasPaths ? (
							<>
								<div className="blk-h">
									<span className="cx-h">
										Ready in <b>{found.conn.paths[0]!.split("/").pop()}</b>
										<span className="dim"> on {found.device.name}</span>
									</span>
								</div>
								<FollowUps apps={[app]} path={found.conn.paths[0]!} />
							</>
						) : found && repos.length ? (
							<>
								<div className="blk-h">
									<span className="cx-h">Which repository should agents work in?</span>
								</div>
								<RepoPicker
									device={found.device}
									label="Connect this repository"
									fixedApps={[app]}
									onResult={setAttach}
								/>
							</>
						) : looking ? (
							<div className="blk-row">
								<span className="spin" aria-hidden />
								<span className="dim2">
									{found ? "Finding your repositories…" : `Finding ${deviceName}…`}
								</span>
								<span className="grow" />
								<span className="mono dim">{mmss(sinceLook)}</span>
							</div>
						) : (
							<>
								<div className="blk-row cx-col">
									<span className="dim2">
										{found
											? `No recent repositories found on ${found.device.name}.`
											: `${deviceName} hasn’t reported in yet.`}{" "}
										You can still open a chat; it joins as an agent.
									</span>
									<div className="row">
										<button
											className="btn btn-xs"
											onClick={() => setLookFrom(Date.now())}
										>
											Look again
										</button>
									</div>
								</div>
								<FollowUps apps={[app]} path={null} />
							</>
						)}
						<div className="blk-row">
							<button className="btn btn-xs btn-ghost" onClick={props.onClose}>
								Done
							</button>
						</div>
					</section>
				))}
		</>
	)
}

// --- existing devices & connections --------------------------------------------------------

function Devices(props: {
	data: DevicesResponse
	latest: string | null
	reload: () => Promise<void>
}) {
	return (
		<div className="cx-devices">
			<div className="lbl">Connections</div>
			{props.data.devices.map(d => (
				<DeviceBlock
					key={d.id}
					d={d}
					live={props.data.live}
					online={isOnline(d, props.data.live, props.data.liveDevices)}
					latest={props.latest}
					reload={props.reload}
				/>
			))}
		</div>
	)
}

function DeviceBlock(props: {
	d: Device
	live: string[]
	online: boolean
	latest: string | null
	reload: () => Promise<void>
}) {
	const { d } = props
	const project = useStore(s => currentProject(s))
	const [confirm, setConfirm] = useState(false)
	const anyLive = props.online
	const outdated =
		props.latest &&
		props.latest !== "dev" &&
		d.connector_version &&
		d.connector_version !== props.latest

	return (
		<div className="blk">
			<div className="blk-h">
				<span
					className={"sockdot " + (anyLive ? "sock-live" : "sock-offline")}
					role="img"
					aria-label={anyLive ? "online" : "offline"}
				/>
				<b className="ell">{d.name}</b>
				<span className="mono dim">v{d.connector_version ?? "?"}</span>
				{outdated && (
					<button
						className="btn btn-xs"
						onClick={() =>
							api
								.updateDevice(d.id)
								.then(r =>
									toast(
										r.sent
											? "Update sent to the connector"
											: "Device is offline; it updates when it reconnects",
										"info",
									),
								)
								.catch(e => toast(errorText(e)))
						}
					>
						Update to v{props.latest}
					</button>
				)}
				<span className="grow" />
				<span className="dim" style={{ fontSize: 11 }}>
					{d.last_seen_at ? `seen ${age(d.last_seen_at, Date.now())} ago` : "never seen"}
				</span>
				{confirm ? (
					<>
						<button
							className="btn btn-xs btn-danger"
							onClick={() =>
								api
									.removeDevice(d.id)
									.then(props.reload)
									.catch(e => toast(errorText(e)))
							}
						>
							Remove all
						</button>
						<button className="btn btn-xs btn-ghost" onClick={() => setConfirm(false)}>
							Keep
						</button>
					</>
				) : (
					<button
						className="btn btn-xs btn-ghost btn-danger"
						title="Disconnect every project on this device"
						onClick={() => setConfirm(true)}
					>
						Remove
					</button>
				)}
			</div>
			{[...d.connections]
				.sort(
					(a, b) =>
						Number(b.project_id === project?.id) - Number(a.project_id === project?.id),
				)
				.map(c => (
					<ConnectionRow
						key={c.id}
						c={c}
						live={props.live.includes(c.id)}
						reload={props.reload}
					/>
				))}
			{!d.connections.length && (
				<div className="blk-row dim">No projects connected on this device.</div>
			)}
		</div>
	)
}

function ConnectionRow({
	c,
	live,
	reload,
}: {
	c: Connection
	live: boolean
	reload: () => Promise<void>
}) {
	const [busy, setBusy] = useState(false)
	const run = async (p: Promise<unknown>, ok?: string) => {
		setBusy(true)
		try {
			await p
			if (ok) toast(ok, "info")
			await reload()
		} catch (e) {
			toast(errorText(e))
		} finally {
			setBusy(false)
		}
	}
	const health = Object.entries(c.health?.apps ?? {})
	const problems = health.filter(([, h]) => h.status !== "ok")
	return (
		<div className="blk-row cx-col cx-conn">
			<div className="row">
				<span
					className={"sockdot " + (live ? "sock-live" : "sock-offline")}
					role="img"
					aria-label={live ? "live" : "offline"}
				/>
				<b>{c.project_name}</b>
				<span className="mono dim ell grow" title={c.paths.join("\n")}>
					{c.repo_key ?? c.paths[0] ?? ""}
				</span>
				{health.map(([app, h]) => (
					<span
						key={app}
						className={"health h-" + h.status}
						title={`${app}: ${h.detail}`}
					>
						{H_GLYPH[h.status]} <span className="dim2">{app}</span>
					</span>
				))}
				{!health.length && <span className="dim">no health yet</span>}
			</div>
			{problems.map(([app, h]) => (
				<div key={app} className={"health h-" + h.status + " cx-indent"}>
					<span className="dim ell" title={h.detail}>
						{app}: {h.detail}
					</span>
				</div>
			))}
			<div className="row cx-indent">
				<label className="check" style={{ fontSize: 11 }}>
					<input
						type="checkbox"
						className="switch"
						checked={c.auto_share}
						disabled={busy}
						onChange={e => void run(api.setAutoShare(c.id, e.target.checked))}
					/>
					auto-share new chats
				</label>
				<span className="grow" />
				<button
					className="btn btn-xs"
					disabled={busy}
					onClick={() => void run(api.repair(c.id), "Repair sent")}
				>
					Repair
				</button>
				<button
					className="btn btn-xs btn-ghost btn-danger"
					disabled={busy}
					onClick={() => void run(api.disconnect(c.id), "Disconnected")}
				>
					Disconnect
				</button>
			</div>
		</div>
	)
}
